import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Lock, PanelLeft } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { filterMenusWithPlan } from '../../routes';
import { useEstabsContext } from '../../contexts/EstabsContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { usePlanAccess } from '../../plans/usePlanAccess';
import { Notifications } from './Notifications';
import { Conta } from './Conta';
import { Logo } from '../Logo';
import MenuTooltip from './MenuTooltip';
import Dropdown from '../../modules/agenda/estabelecimento/components/Dropdown';
import { Modal } from '../../components/Modal';
import { ImageEstab } from '../../modules/configuracoes/estabelecimento/Form/ImageEstab';
import { EstabelecimentosService } from '../../modules/estabelecimentos/system-admin/Service';
import { getMenuSectionKey, MENU_SECTIONS } from './menuSections';
import EstablishmentSwitcher from './EstablishmentSwitcher';

export const MENU_WIDTHS = {
    collapsed: 88,
    expanded: 260,
    subMenu: 280,
    gap: 16,
    offset: 32,
};

const estabelecimentosService = new EstabelecimentosService();

export default function Menu() {
    const { setSubMenu, toggleShortMenu, shortMenu, subMenu: activeSubMenu, successSnackbar, errorSnackbar } =
        useAppContext();
    const [openLogoModal, setOpenLogoModal] = useState(false);
    const [logoImageId, setLogoImageId] = useState('');
    const [logoSaving, setLogoSaving] = useState(false);
    const [confirmRemoveLogo, setConfirmRemoveLogo] = useState(false);

    const { user } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { estabSession, reloadEstab } = useEstabsContext();
    const { menuShowEntry, hasFeature, restrictionsApply } = usePlanAccess();

    const visibleMenus = filterMenusWithPlan(user, menuShowEntry);

    useEffect(() => {
        setLogoImageId(estabSession?.imageId || '');
    }, [estabSession?.imageId]);

    const isExpanded = !shortMenu;
    const menuWidth = shortMenu ? MENU_WIDTHS.collapsed : MENU_WIDTHS.expanded;
    const toggleLabel = shortMenu ? 'Expandir menu' : 'Recolher menu';
    const isClientOnly = !!user?.client && !user?.admin && !user?.worker && !user?.root;
    const canManageLogo = !!(user?.admin || user?.root);
    const shouldShowMenuSections = isExpanded && !isClientOnly;

    const getItemClassName = (isActive = false) =>
        [
            'sf-menu-item flex w-full items-center gap-3 rounded-lg text-sm font-medium',
            isExpanded ? 'min-h-10 px-3 py-2 text-left' : 'h-10 justify-center px-0',
            isActive ? 'is-active' : '',
        ].join(' ');

    const handleNavigate = (path: string, nextSubMenu: any) => {
        navigate(path);
        setSubMenu(nextSubMenu);
    };

    const groupedMenus = MENU_SECTIONS.map((section) => ({
        ...section,
        items: visibleMenus.filter((menu) => getMenuSectionKey(menu.path) === section.key),
    })).filter((section) => section.items.length > 0);

    const otherMenus = visibleMenus.filter((menu) => getMenuSectionKey(menu.path) === 'outros');

    const renderMenuItem = ({ title, icon: Icon, path, subMenu, planFeature }: any, index: number) => {
        const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

        const lockedByPlan = !!(
            restrictionsApply &&
            planFeature &&
            (user?.admin || user?.root) &&
            !hasFeature(planFeature)
        );

        const item = (
            <li>
                <button
                    type="button"
                    className={getItemClassName(isActive) + (lockedByPlan ? ' opacity-[0.72]' : '')}
                    onClick={() => handleNavigate(path, lockedByPlan ? false : subMenu)}
                    aria-label={!isExpanded ? title : undefined}
                    aria-current={isActive ? 'page' : undefined}
                >
                    <Icon className="sf-menu-item-icon" aria-hidden="true" />
                    {isExpanded && <span className="min-w-0 flex-1 truncate">{title}</span>}
                    {isExpanded && lockedByPlan && (
                        <Lock size={12} strokeWidth={2} className="shrink-0 opacity-60" aria-hidden="true" />
                    )}
                </button>
            </li>
        );

        return (
            <MenuTooltip key={`${path}-${index}`} label={title} enabled={shortMenu}>
                {item}
            </MenuTooltip>
        );
    };

    const saveLogoImage = async () => {
        if (!estabSession?._id) {
            return;
        }

        try {
            setLogoSaving(true);
            await estabelecimentosService.update(estabSession._id, {
                imageId: logoImageId || '',
            });
            await reloadEstab();
            successSnackbar('Logo do estabelecimento atualizada com sucesso!');
            setOpenLogoModal(false);
        } catch (error) {
            console.error('Erro ao atualizar logo do estabelecimento:', error);
            errorSnackbar('Não foi possível atualizar a logo do estabelecimento.');
        } finally {
            setLogoSaving(false);
        }
    };

    const removeLogoImage = async () => {
        if (!estabSession?._id) {
            return;
        }

        try {
            setLogoSaving(true);
            await estabelecimentosService.update(estabSession._id, {
                imageId: '',
            });
            await reloadEstab();
            successSnackbar('Logo do estabelecimento removida com sucesso!');
            setConfirmRemoveLogo(false);
        } catch (error) {
            console.error('Erro ao remover logo do estabelecimento:', error);
            errorSnackbar('Não foi possível remover a logo do estabelecimento.');
        } finally {
            setLogoSaving(false);
        }
    };

    return (
        <div className="flex items-start gap-4">
            {openLogoModal && (
                <Modal
                    onClose={() => setOpenLogoModal(false)}
                    title="Logo do estabelecimento"
                    subTitle="Escolha uma nova imagem ou remova a atual."
                >
                    <div className="space-y-5">
                        <ImageEstab imageId={logoImageId} setImageId={setLogoImageId} />
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                className="btn btn-ghost w-full sm:w-auto"
                                onClick={() => setOpenLogoModal(false)}
                                disabled={logoSaving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary w-full sm:w-auto"
                                onClick={saveLogoImage}
                                disabled={logoSaving}
                            >
                                {logoSaving ? 'Salvando...' : 'Salvar imagem'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {confirmRemoveLogo && (
                <Modal
                    onClose={() => setConfirmRemoveLogo(false)}
                    title="Remover logo?"
                    subTitle="O estabelecimento voltará a exibir a logo padrão."
                >
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            className="btn btn-ghost w-full sm:w-auto"
                            onClick={() => setConfirmRemoveLogo(false)}
                            disabled={logoSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-error w-full sm:w-auto"
                            onClick={() => {
                                void removeLogoImage();
                            }}
                            disabled={logoSaving}
                        >
                            {logoSaving ? 'Removendo...' : 'Remover'}
                        </button>
                    </div>
                </Modal>
            )}

            <div className="relative" style={{ width: `${menuWidth}px` }}>
                <nav
                    className="sf-menu-shell relative z-40 flex h-[calc(100vh-32px)] flex-col rounded-[1.35rem] p-2 transition-[width] duration-200"
                    style={{
                        width: `${menuWidth}px`,
                    }}
                    aria-label="Menu principal"
                >
                    <div className="px-1 pb-3 pt-1">
                        {!isExpanded ? (
                            <div className="flex items-center justify-center">
                                <button
                                    type="button"
                                    className="sf-sidebar-toggle mt-1"
                                    onClick={toggleShortMenu}
                                    aria-label={toggleLabel}
                                    aria-expanded={isExpanded}
                                    title={toggleLabel}
                                >
                                    <PanelLeft size={18} strokeWidth={1.75} aria-hidden="true" />
                                </button>
                            </div>
                        ) : (
                            <div className="py-2 pl-3 pr-1">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-start">
                                        {/* Alterar/remover logo e acao de gestao: so admin e root. */}
                                        {canManageLogo ? (
                                            <Dropdown
                                                style={{ left: '50%', right: 'auto', transform: 'translateX(-50%)' }}
                                                options={[
                                                    {
                                                        label: 'Alterar imagem',
                                                        onClick: () => {
                                                            setLogoImageId(estabSession?.imageId || '');
                                                            setOpenLogoModal(true);
                                                        },
                                                    },
                                                    ...(estabSession?.imageId
                                                        ? [
                                                              {
                                                                  label: 'Remover imagem',
                                                                  onClick: () => setConfirmRemoveLogo(true),
                                                              },
                                                          ]
                                                        : []),
                                                ]}
                                            >
                                                <div className="flex items-center justify-center rounded-2xl focus:outline-none">
                                                    <Logo interactive={false} />
                                                </div>
                                            </Dropdown>
                                        ) : (
                                            <Logo interactive={false} />
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="sf-sidebar-toggle ml-auto shrink-0"
                                        onClick={toggleShortMenu}
                                        aria-label={toggleLabel}
                                        aria-expanded={isExpanded}
                                        title={toggleLabel}
                                    >
                                        <PanelLeft size={18} strokeWidth={1.75} aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="menu-scroll no-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-2">
                        <ul className={`menu gap-0.5 ${isExpanded ? 'px-1' : 'px-3'}`}>
                            {isClientOnly ? visibleMenus.map((menu, index) => renderMenuItem(menu, index)) : (
                                [...groupedMenus, ...(otherMenus.length ? [{ key: 'outros', title: 'Outros', items: otherMenus }] : [])]
                                    .map((section, sectionIndex) => (
                                        <li
                                            key={section.key}
                                            className={`menu-section sf-menu-group ${sectionIndex > 0 ? 'mt-4' : ''}`}
                                            aria-label={section.title}
                                        >
                                            {shouldShowMenuSections && (
                                                <div className="sf-menu-section-label px-3 pb-1 pt-1 text-[11px] font-semibold text-base-content/45">
                                                    {section.title}
                                                </div>
                                            )}
                                            <ul className="menu gap-0.5 p-0">
                                                {section.items.map((menu, index) => renderMenuItem(menu, index))}
                                            </ul>
                                        </li>
                                    ))
                            )}
                        </ul>
                    </div>

                    <div className="sf-menu-footer mt-auto px-1 pb-1 pt-3">
                        <ul className={`menu gap-0.5 ${isExpanded ? 'px-0' : ''}`}>
                            <Notifications expanded={isExpanded} showTooltip={shortMenu} />
                            <Conta expanded={isExpanded} showTooltip={shortMenu} />

                            <EstablishmentSwitcher expanded={isExpanded} />
                        </ul>
                    </div>
                </nav>
            </div>

            {!!activeSubMenu && (
                <aside
                    className={`relative z-10 h-[calc(100vh-32px)] overflow-y-auto ${
                        location.pathname === '/' ? '' : 'sf-submenu-shell rounded-[1.35rem]'
                    }`}
                    style={{ width: `${MENU_WIDTHS.subMenu}px` }}
                    aria-label="Painel lateral"
                >
                    {activeSubMenu}
                </aside>
            )}
        </div>
    );
}
